import React, { useState, useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';

import {
  Button,
  FormGroup,
  ModalContent,
  RotateLoader,
  TabSelect,
} from '@lib/components';
import { Modal } from '@lib/components';
import { LimitsTab } from './limits-tab';
import { ConditionTab } from './condition-tab';
import { FreeItemPromo, ConventionalDiscountPromo, NewGenericPromo } from '../promos/type';
import { UI } from '../../../../../../../../core/ui';
import { GeneralInformationTab } from './general-tab';
import { FreeItemPromoValidationSchema } from './validationSchema';
import { RestaurantForm } from '../../../../../../../../mobx/components/restaurant-form';
import { ConditionDiscountPromoTab } from './condition-discount-promo-tab';
import { ATTRIBUTE_MAP } from '../promos/constants';
import { PromoParser } from '@lib/common';

interface Props {
  type: 'edit' | 'create';
  promoType: 'free_item' | 'conventional_discount';
  initialValues: NewGenericPromo;
  close: () => void;
}

export const SettingsFreeItemPromosForm = (props: Props) => {
  const [tab, setTab] = useState('0');
  const { store } = useContext(MobXProviderContext);
  const restaurant = store.restaurant;
  const { close, initialValues: currentPromo, promoType } = props;
  const promoList: any[] = [
    ...restaurant.promos,
    ...(restaurant.free_item_promos || []),
    ...(restaurant.conventional_discount_promos || []),
  ];
  const promoCodeList = promoList.map(promo => promo.code.toLowerCase());

  const updatePromosQuery = (
    restaurant: T.Schema.Restaurant.RestaurantSchema,
    item: FreeItemPromo
  ) => {
    const promos = restaurant[ATTRIBUTE_MAP[promoType]] || [];
    const idx = promos.findIndex((p: any) => p._id === item._id);
    if (idx === -1) {
      promos.push(item);
    } else {
      promos[idx] = item;
    }

    // @ts-ignore
    restaurant[ATTRIBUTE_MAP[promoType]] = promos;

    return {
      $set: { [ATTRIBUTE_MAP[promoType]]: promos },
    };
  };

  const updateWalletlyPromo = async (restaurant :T.Schema.Restaurant.RestaurantSchema, item: NewGenericPromo) => {
    const promoParser = new PromoParser(item);
    const isDisable = promoParser.isDisable();
    const dealId = promoParser.getDeal();
    if (dealId) {
      const response = await store.api.updateDeal({
        _id: restaurant._id,
        dealId: dealId,
        valid: !isDisable
      });

      if (response.outcome) {
        throw new Error("An error occurred");
      }
      return {
        r: restaurant,
        // @ts-ignore
        update: updatePromosQuery(restaurant, item),
      };
    }

    return {
      r: restaurant,
      // @ts-ignore
      update: updatePromosQuery(restaurant, item),
    };
  }

  return (
    <Modal
      width="md"
      close={close}
      alignTop={true}
      active={!!currentPromo}
      id="free-item-promo"
    >
      <ModalContent className="">
        <h3 className="">{`${
          currentPromo.code ? 'Update' : 'Create'
        } Promo: ${promoType === 'free_item' ? 'Free Item' : 'Discount'} `}</h3>
      </ModalContent>

      <TabSelect
        id="free-item-promo-tab"
        className="border-white-tb-10"
        hasBorder={true}
        screenWidth={store.view.screen_width}
        onChange={v => setTab(v.value)}
        value={tab}
        values={[
          { label: 'General', value: '0' },
          { label: 'Condition', value: '1' },
          { label: 'Limits', value: '2' },
        ]}
      />

      <RestaurantForm<FreeItemPromo | ConventionalDiscountPromo>
        initialValues={currentPromo}
        validators={{
          code: values => {
            if (
              !currentPromo.code &&
              promoCodeList.some(e => e === values.code.toLowerCase())
            )
              return { code: 'This code is already taken' };
          },
        }}
        validationSchema={FreeItemPromoValidationSchema}
        onSuccess={() => close()}
        onError={() => UI.notification.error('An error occurred')}
        onSuccessMessage="Settings Updated"
        submit={async (restaurant, item) => (
          updateWalletlyPromo(restaurant, item)
        )}
      >
        {({ form, getFieldError }) => {
          const { isSubmitting, errors, values } = form;
          return (
            <>
              {tab === '0' && (
                <GeneralInformationTab
                  form={form}
                  getFieldError={getFieldError}
                  promoType={promoType}
                />
              )}

              {tab === '1' && (
                <>
                {promoType === 'free_item' && (
                  <ConditionTab form={form} getFieldError={getFieldError} />
                )}
                {promoType === 'conventional_discount' && (
                  <ConditionDiscountPromoTab form={form} getFieldError={getFieldError} />
                )}
                </>
              )}

              {tab === '2' && (
                <LimitsTab form={form} getFieldError={getFieldError} />
              )}

              <ModalContent>
                <Button
                  full={true}
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <RotateLoader size={2} color="white" />}
                  {!isSubmitting && 'Save'}
                </Button>

                {errors && Object.keys(errors).length > 0 && (
                  <FormGroup
                    no_border
                    error="Validation failed. Please check the promotion data again."
                  />
                )}
              </ModalContent>
            </>
          );
        }}
      </RestaurantForm>
    </Modal>
  );
};
